import { useEffect, useRef, type RefObject } from 'react';

/**
 * Call `handler` when Escape is pressed, while `active`. Used by every dismissible
 * overlay (menus, drawer, modal) so the keyboard-dismiss contract lives in one place.
 */
export function useOnEscape(active: boolean, handler: () => void): void {
  useEffect(() => {
    if (!active) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        handler();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [active, handler]);
}

/**
 * Call `handler` on a pointer-down outside `ref`, while `active`. The dismissal
 * half of menus and popovers.
 */
export function useOnClickOutside(
  ref: RefObject<HTMLElement | null>,
  active: boolean,
  handler: () => void,
): void {
  useEffect(() => {
    if (!active) return;
    const onDown = (event: MouseEvent): void => {
      const el = ref.current;
      if (el !== null && event.target instanceof Node && !el.contains(event.target)) {
        handler();
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
    };
  }, [ref, active, handler]);
}

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function focusable(container: HTMLElement): HTMLElement[] {
  // The selector already excludes disabled controls and tabindex=-1. We avoid an
  // `offsetParent` visibility filter here because jsdom never lays out, so it
  // would report every element as hidden; our overlays only mount their
  // focusables when open, so this is safe.
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => el.getAttribute('aria-hidden') !== 'true',
  );
}

/**
 * Trap Tab focus inside `ref` while `active`, move initial focus in, and restore
 * focus to the previously-focused element on close. The dialog/drawer half of
 * WCAG 2.4.3 + 2.1.2.
 */
export function useFocusTrap(ref: RefObject<HTMLElement | null>, active: boolean): void {
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const container = ref.current;
    if (!active || container === null) return;

    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const initial = focusable(container)[0] ?? container;
    initial.focus();

    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Tab') return;
      const items = focusable(container);
      if (items.length === 0) {
        event.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const activeEl = document.activeElement;
      if (event.shiftKey && activeEl === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && activeEl === last) {
        event.preventDefault();
        first?.focus();
      }
    };

    container.addEventListener('keydown', onKey);
    return () => {
      container.removeEventListener('keydown', onKey);
      previouslyFocused.current?.focus();
    };
  }, [ref, active]);
}

/**
 * Fire `tick` `intervalMs` after each settled load, while `active`.
 *
 * Deliberately a re-armed `setTimeout` rather than a `setInterval`: the clock
 * starts when a load *finishes*, so a response slower than the interval can
 * never stack a queue of overdue refreshes behind itself, and a poll answering
 * "nothing is in flight any more" ends the loop instead of merely skipping a
 * beat. `restartKey` is what marks a settled load — pass a value that changes
 * once per completed fetch (a load counter, or the loaded state's identity).
 *
 * `tick` is held in a ref, so an inline closure does not restart the clock on
 * every render.
 */
export function usePolling(
  active: boolean,
  intervalMs: number,
  restartKey: unknown,
  tick: () => void,
): void {
  const latest = useRef(tick);
  latest.current = tick;

  useEffect(() => {
    if (!active) return;
    const id = setTimeout(() => {
      latest.current();
    }, intervalMs);
    return () => {
      clearTimeout(id);
    };
  }, [active, intervalMs, restartKey]);
}
